/**
 * search_knowledge tool — Task D2
 *
 * Deterministic, keyed retrieval over the curated in-repo knowledge corpus.
 * No vector store / embeddings — retrieval is exact by id, exact by type, or
 * lexical substring (case-insensitive) over title+summary+keys+body.
 *
 * Precedence (first non-empty argument wins):
 *   ids  → kb.get(ids)       — full KnowledgeUnit bodies for those ids
 *   type → kb.byType(type)   — all full units of that UnitType
 *   text → kb.search(text)   — lexical substring match over the haystack
 *   (none) → kb.catalog()    — UnitMeta array (id/type/title/summary; NO body)
 *
 * Design notes:
 * - The model should call with NO args first to receive the catalog, then call
 *   again with specific `ids` to retrieve full unit bodies.
 * - Non-object input is rejected with isError:true; nothing escapes run().
 */

import type { KnowledgeProvider, UnitType } from '../grounding/types'
import type { AdvisorTool, ToolResult } from '../runtime/types'

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeSearchKnowledgeTool(kb: KnowledgeProvider): AdvisorTool {
  return {
    name: 'search_knowledge',

    description:
      'Retrieves curated product knowledge about pages, KPIs, dimensions, concepts, and ' +
      'routing rules. ' +
      'Call with NO arguments first — you will receive the CATALOG (a list of unit ' +
      'titles/summaries) so you can discover what is available. ' +
      'Then call again with `ids` (an array of unit id strings) to fetch the FULL bodies of ' +
      'those specific units. ' +
      'Alternatively: pass `type` ("page", "kpi", "dim", "concept", "routing", or "finding") ' +
      'to get all units of that type, or pass `text` to run a case-insensitive lexical search across ' +
      'titles, summaries, keys, and bodies. ' +
      'Precedence when multiple fields are supplied: ids > type > text > catalog.',

    input_schema: {
      type: 'object',
      properties: {
        ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'One or more unit ids to fetch in full. Takes highest precedence. ' +
            'Use the catalog (no-arg call) to discover valid ids.',
        },
        type: {
          type: 'string',
          enum: ['page', 'kpi', 'dim', 'concept', 'routing', 'finding'],
          description: 'Return all units of this type. Ignored when `ids` is non-empty.',
        },
        text: {
          type: 'string',
          description:
            'Case-insensitive substring to search across title, summary, keys, and body. ' +
            'Ignored when `ids` or `type` is non-empty.',
        },
      },
      // No fields are required — calling with no args returns the catalog.
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        // ------------------------------------------------------------------
        // 1. Narrow input — must be a plain object (not null, not array)
        // ------------------------------------------------------------------
        if (typeof input !== 'object' || input === null || Array.isArray(input)) {
          return {
            content: 'search_knowledge: input must be a JSON object (or omitted for catalog)',
            isError: true,
          }
        }

        const inp = input as Record<string, unknown>

        // ------------------------------------------------------------------
        // 2. Extract fields with safe narrowing
        // ------------------------------------------------------------------
        const rawIds = inp['ids']
        const ids: string[] | undefined =
          Array.isArray(rawIds) && rawIds.length > 0
            ? (rawIds as unknown[]).filter((x): x is string => typeof x === 'string')
            : undefined

        const rawType = inp['type']
        const type: string | undefined =
          typeof rawType === 'string' && rawType.length > 0 ? rawType : undefined

        const rawText = inp['text']
        const text: string | undefined =
          typeof rawText === 'string' && rawText.length > 0 ? rawText : undefined

        // ------------------------------------------------------------------
        // 3. Dispatch by precedence: ids → type → text → catalog
        // ------------------------------------------------------------------
        let result: unknown

        if (ids !== undefined && ids.length > 0) {
          result = kb.get(ids)
        } else if (type !== undefined) {
          result = kb.byType(type as UnitType)
        } else if (text !== undefined) {
          result = kb.search(text)
        } else {
          result = kb.catalog()
        }

        // ------------------------------------------------------------------
        // 4. Return JSON-serialised result
        // ------------------------------------------------------------------
        return { content: JSON.stringify(result) }
      } catch (err) {
        // ------------------------------------------------------------------
        // 5. Nothing escapes run()
        // ------------------------------------------------------------------
        return {
          content: err instanceof Error ? err.message : String(err),
          isError: true,
        }
      }
    },
  }
}
